import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Download, Save, FileSignature, ArrowRight } from 'lucide-react';
import { genUuid } from '@/lib/uuid';
import { useQuoteDraftStore } from '../state/useQuoteDraftStore';
import { clearQuoteDraftFromIdb } from '../state/quoteDraftIdbSync';
import { getNextQuoteCounter } from '../state/quoteCounter';
import {
  loadLatestSavedQuoteByDraftKey,
  loadSavedQuote,
  saveQuoteWithState,
} from '../state/savedQuotesIdb';
import { generateAndDownloadQuotePdf } from '../services/generatePdf';
import { ShareBottomSheet } from '../components/ShareBottomSheet';
import { buildDesktopShareToast, type ShareResult } from '../services/shareQuotePdf';
import type { QuoteState } from '../types';

/**
 * Block 12 PR-D-A нижние CTA-кнопки — Share + Download PDF + Save + Сделать договор.
 *
 * **PR-H6 (ADR-0029) auto-save:** при нажатии любой из 3 actions (Переслать /
 * Скачать PDF / Сохранить) — `ensureQuoteSaved()` сохраняет КП в IDB с
 * composite name «Коммерческое предложение №N — {Furniture}». Idempotent
 * через savedQuoteIdRef + in-flight Promise guard (защита от parallel
 * double-click race). После первого save «Сделать договор» Link становится
 * enabled автоматически (4-я кнопка — disabled placeholder → Link через
 * savedQuoteId state).
 *
 * **handleSave flow (PR-H6 refactor):**
 *  - Calls `ensureQuoteSaved()` (no-op если уже сохранён в session).
 *  - UI feedback unchanged: saveStatus state + toast «Сохранён: {name}».
 *
 * **handleDownloadPdf flow (PR-H6 refactor):**
 *  1. `ensureQuoteSaved()` → returns {savedName} (auto-saves если не сохранён).
 *  2. `generateAndDownloadQuotePdf(state, savedName)` → composite title в PDF.
 *
 * **handleOpenShare flow (PR-H6 NEW):**
 *  1. `ensureQuoteSaved()` → returns {savedName}.
 *  2. `setShareOpen(true)` → ShareBottomSheet получает quoteName через
 *     existing `savedName` state prop (unchanged signature).
 *
 * **«Сделать договор» (4-я кнопка):** explicit auto-save НЕ нужен — Link
 * renders когда savedQuoteId !== null, что естественно set'ится в первом
 * ensureQuoteSaved call (любой из 3 actions). Если user сразу клик
 * «Сделать договор» без предварительных actions — disabled placeholder
 * с hint «Сначала сохраните КП», как и до PR-H6.
 *
 * **Validation guards** (mirror existing handleSave):
 *  - `furnitureType === null` → throw «Выбери тип мебели»
 *  - `costWithMargin === null || <= 0` → throw «Введи положительную стоимость»
 *  Каждый handler catch'ит и push'ит в setErrorMessage. UI inline error.
 *
 * **Draft cleanup:** mirror PR-D5 Phase pattern — после save через
 * ensureQuoteSaved → `clearDraft(Zustand)` + `clearQuoteDraftFromIdb(IDB)`.
 *
 * Reference: ADR-0029, TZ Phase D5 Step 11 + D6 Step 13 + D7 Step 14 + D8 Step 15,
 * PRD §10.12.10 «Кнопки внизу».
 */
export interface BottomActionButtonsProps {
  state: QuoteState;
  draftKey: string;
  /**
   * PR-C1 (ADR-0035) Phase 1 — путь к существующему Договору по этому КП
   * (`/contracts/<id>/edit`, последний по updatedAt), либо null. Вычисляется
   * в контейнере (`QuotePageContainer` через `useForwardToContract`, ADR-0005).
   * null → кнопка «Вперёд» скрыта (решение #1 — нет downstream → нет кнопки).
   */
  forwardContractPath?: string | null;
}

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';
type PdfStatus = 'idle' | 'rendering' | 'error';

/**
 * PR-H7 (ADR-0030 §9) — extended SavedRef tracks `furnitureType` + `counter`
 * для re-save behavior. При смене state.furnitureType после initial save —
 * `ensureQuoteSaved` обновляет existing IDB record (same id, same counter,
 * new composite name), а не создаёт новую запись.
 */
interface SavedRef {
  id: string;
  name: string;
  furnitureType: string;
  counter: number;
}

/**
 * Validation error messages из ensureQuoteSaved которые user-facing — должны
 * показаться inline без generic fallback. Runtime errors (IDB unavailable,
 * PDF render fail) идут через generic fallback per error semantics.
 */
const VALIDATION_ERROR_MESSAGES = new Set<string>([
  'Выбери тип мебели',
  'Введи положительную стоимость',
]);

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && VALIDATION_ERROR_MESSAGES.has(err.message)) {
    return err.message;
  }
  return fallback;
}

/**
 * PR-C2 Phase 2 fix (ADR-0036 A1) — вытащить counter №N из имени КП
 * «Коммерческое предложение №N — {Тип}» для reuse при пересохранении. null,
 * если формат не распознан (caller fallback'ит на getNextQuoteCounter).
 */
function parseQuoteCounter(name: string): number | null {
  const m = name.match(/№(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

export function BottomActionButtons({
  state,
  draftKey,
  forwardContractPath,
}: BottomActionButtonsProps) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [savedName, setSavedName] = useState<string | null>(null);
  const [savedQuoteId, setSavedQuoteId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pdfStatus, setPdfStatus] = useState<PdfStatus>('idle');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareNotification, setShareNotification] = useState<{
    kind: 'success' | 'info';
    text: string;
  } | null>(null);

  // Refs для idempotency check внутри ensureQuoteSaved — synchronous source
  // of truth (state updates async через React, refs always current).
  const savedRef = useRef<SavedRef | null>(null);
  // In-flight guard: если parallel calls (double-click race) пришли до того
  // как первый save завершился — обa получат тот же Promise.
  const ensureSavePromiseRef = useRef<Promise<SavedRef> | null>(null);

  // Reset cached saved-ref when draftKey changes — different draftKey =
  // different КП в разработке, must start fresh save flow.
  useEffect(() => {
    savedRef.current = null;
    ensureSavePromiseRef.current = null;
    setSavedName(null);
    setSavedQuoteId(null);
    setSaveStatus('idle');
    setErrorMessage(null);
  }, [draftKey]);

  const canSave =
    state.furnitureType !== null &&
    state.costWithMargin !== null &&
    state.costWithMargin > 0 &&
    saveStatus !== 'saving';

  const canDownloadPdf =
    state.furnitureType !== null && state.hydrationStatus === 'ready' && pdfStatus !== 'rendering';

  const canShare = state.furnitureType !== null && state.hydrationStatus === 'ready';

  /**
   * PR-H6 (ADR-0029) + PR-H7 (ADR-0030 §9) — auto-save helper.
   *
   * **3 cases:**
   *   1. **First save** (savedRef.current === null): validate state, get
   *      counter, generate composite name «Коммерческое предложение №N —
   *      {Furniture}», save в IDB, clear draft.
   *   2. **Re-save при смене furnitureType** (savedRef set + change detected):
   *      update existing IDB record (same id, same counter, new composite name).
   *      Counter remains, drafts list показывает ONE entry.
   *   3. **Cached return** (savedRef set + same furnitureType): no-op, return
   *      cached SavedRef.
   *
   * Throws on validation failures либо IDB errors — каждый caller catch'ит
   * и push'ит в setErrorMessage. In-flight Promise guard защищает от parallel
   * calls (double-click race).
   */
  async function ensureQuoteSaved(): Promise<SavedRef> {
    // PR-H7: in-flight guard покрывает оба Case 1 (first save) и Case 2
    // (re-save при смене furnitureType). Защищает от double-click race +
    // одновременно reuse pending promise если parallel caller пришёл сразу
    // после первого click.
    if (ensureSavePromiseRef.current !== null) {
      return ensureSavePromiseRef.current;
    }

    // Case 3: cached return — savedRef set + state.furnitureType unchanged
    if (
      savedRef.current !== null &&
      (state.furnitureType === null || savedRef.current.furnitureType === state.furnitureType)
    ) {
      return savedRef.current;
    }

    // Case 2: re-save при смене furnitureType (savedRef set + furnitureType changed)
    if (savedRef.current !== null && state.furnitureType !== null) {
      const previous = savedRef.current;
      const nextFurnitureType = state.furnitureType;
      const promise = resaveOnFurnitureTypeChange(previous, nextFurnitureType);
      ensureSavePromiseRef.current = promise;
      try {
        return await promise;
      } finally {
        ensureSavePromiseRef.current = null;
      }
    }

    // Case 1: first save
    if (state.furnitureType === null) {
      throw new Error('Выбери тип мебели');
    }
    if (state.costWithMargin === null || state.costWithMargin <= 0) {
      throw new Error('Введи положительную стоимость');
    }

    const furnitureType = state.furnitureType;
    const promise = (async (): Promise<SavedRef> => {
      const now = Date.now();

      // PR-C2 Phase 2 fix (ADR-0036 Amendment A1, Вариант 1) — переиспользуем
      // существующий снимок КП для этого ПРОЕКТА (draftKey) вместо нового id.
      // Иначе пересохранение КП после ремаунта плодит новый снимок, и связь
      // Договор↔КП (fromQuoteId) ломается → пересев Договора не срабатывает
      // (Type E, Aibek 2026-05-30) + «Вперёд» КП→Договор скрывается.
      //
      // Только для реального projectId: 'standalone' — общий draftKey для
      // несвязанных КП, его reuse слил бы разные КП в один снимок → для него
      // сохраняем прежнее поведение (новый id + новый counter).
      const existing =
        draftKey !== 'standalone'
          ? await loadLatestSavedQuoteByDraftKey(draftKey).catch(() => undefined)
          : undefined;

      let id: string;
      let counter: number;
      let createdAt: number;
      if (existing) {
        // Update-in-place: тот же id/createdAt/counter (mirror Case 2). Имя
        // пересобирается с текущим furnitureType + сохранённым counter.
        id = existing.id;
        createdAt = existing.createdAt;
        counter = parseQuoteCounter(existing.name) ?? (await getNextQuoteCounter(furnitureType));
      } else {
        counter = await getNextQuoteCounter(furnitureType);
        id = genUuid();
        createdAt = now;
      }
      const name = `Коммерческое предложение №${counter} — ${furnitureType}`;

      await saveQuoteWithState({
        id,
        draftKey,
        name,
        state,
        createdAt,
        updatedAt: now,
      });

      useQuoteDraftStore.getState().clearDraft(draftKey);
      await clearQuoteDraftFromIdb(draftKey);

      const saved: SavedRef = { id, name, furnitureType, counter };
      savedRef.current = saved;
      setSavedName(name);
      setSavedQuoteId(id);
      // NOTE: setSaveStatus НЕ trigger'ится здесь — это responsibility
      // handleSave (показывает «✓ Сохранён» только когда user явно нажал Save).
      // handleDownloadPdf / handleOpenShare через auto-save не должны
      // переключать save button label.
      return saved;
    })();

    ensureSavePromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      ensureSavePromiseRef.current = null;
    }
  }

  /**
   * PR-H7 (ADR-0030 §9) — Case 2 helper: update existing IDB record при смене
   * state.furnitureType. Counter preserved (`previous.counter`), createdAt
   * preserved через `loadSavedQuote`, name regenerated с новым furnitureType.
   * IDB `put` upserts по same id — existing record overwrite.
   *
   * In-flight guard через `ensureSavePromiseRef` обеспечивается caller'ом
   * (`ensureQuoteSaved`) — этот helper не reentrant-safe сам по себе.
   *
   * Known tradeoff: `quote-counters` per-type IDB store не bump'ится для
   * `nextFurnitureType` (см. BACKLOG B-171 — counter collision на genuine
   * future first save с same type. Accepted Aibek vision «counter remains»).
   */
  async function resaveOnFurnitureTypeChange(
    previous: SavedRef,
    nextFurnitureType: string,
  ): Promise<SavedRef> {
    const newName = `Коммерческое предложение №${previous.counter} — ${nextFurnitureType}`;
    const now = Date.now();
    const existing = await loadSavedQuote(previous.id);

    await saveQuoteWithState({
      id: previous.id,
      draftKey,
      name: newName,
      state,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    });

    const updated: SavedRef = {
      id: previous.id,
      name: newName,
      furnitureType: nextFurnitureType,
      counter: previous.counter,
    };
    savedRef.current = updated;
    setSavedName(newName);
    return updated;
  }

  async function handleSave(): Promise<void> {
    setSaveStatus('saving');
    setErrorMessage(null);
    try {
      await ensureQuoteSaved();
      setSaveStatus('success');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[BottomActionButtons] handleSave failed', err);
      }
      setSaveStatus('error');
      setErrorMessage(pickErrorMessage(err, 'Не удалось сохранить. Попробуйте ещё раз.'));
    }
  }

  async function handleDownloadPdf(): Promise<void> {
    setPdfStatus('rendering');
    setErrorMessage(null);
    try {
      const saved = await ensureQuoteSaved();
      await generateAndDownloadQuotePdf(state, saved.name);
      setPdfStatus('idle');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[BottomActionButtons] handleDownloadPdf failed', err);
      }
      setPdfStatus('error');
      setErrorMessage(pickErrorMessage(err, 'Не удалось создать PDF. Попробуйте ещё раз.'));
    }
  }

  async function handleOpenShare(): Promise<void> {
    setShareNotification(null);
    setErrorMessage(null);
    try {
      await ensureQuoteSaved();
      setShareOpen(true);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[BottomActionButtons] handleOpenShare failed', err);
      }
      setErrorMessage(pickErrorMessage(err, 'Не удалось открыть Поделиться. Попробуйте ещё раз.'));
    }
  }

  return (
    <div
      className="mx-3 mt-1 flex flex-col gap-2.5"
      data-testid="section-actions"
      aria-label="Действия по КП"
    >
      <button
        type="button"
        onClick={handleOpenShare}
        disabled={!canShare}
        aria-label="Переслать клиенту"
        data-testid="quote-share-button"
        className="w-full min-h-16 rounded-2xl bg-cta text-ink text-lg font-semibold flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
      >
        <Send size={22} aria-hidden="true" />
        Переслать клиенту
      </button>
      <button
        type="button"
        onClick={handleDownloadPdf}
        disabled={!canDownloadPdf}
        aria-label="Скачать PDF"
        data-testid="quote-pdf-button"
        className="w-full min-h-14 rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
      >
        <Download size={22} aria-hidden="true" />
        {pdfStatus === 'rendering' ? 'Создаётся PDF…' : 'Скачать PDF'}
      </button>
      <button
        type="button"
        onClick={handleSave}
        disabled={!canSave}
        aria-label="Сохранить"
        data-testid="quote-save-button"
        className="w-full min-h-14 rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
      >
        <Save size={22} aria-hidden="true" />
        {saveStatus === 'saving'
          ? 'Сохраняется…'
          : saveStatus === 'success'
            ? '✓ Сохранён'
            : 'Сохранить'}
      </button>
      {savedQuoteId ? (
        <Link
          to={`/contracts/new?fromQuote=${encodeURIComponent(savedQuoteId)}`}
          aria-label="Сделать договор по этому КП"
          data-testid="quote-contract-link"
          className="w-full min-h-14 rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
        >
          <FileSignature size={22} aria-hidden="true" />
          Сделать договор по этому КП
        </Link>
      ) : (
        <button
          type="button"
          disabled
          aria-label="Сделать договор по этому КП"
          aria-describedby="quote-contract-hint"
          data-testid="quote-contract-button-disabled"
          className="w-full min-h-14 rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 opacity-50 cursor-not-allowed"
        >
          <FileSignature size={22} aria-hidden="true" />
          Сделать договор по этому КП
        </button>
      )}
      {!savedQuoteId ? (
        <p
          id="quote-contract-hint"
          className="text-xs text-muted text-center"
          data-testid="quote-contract-hint"
        >
          Сначала сохраните КП.
        </p>
      ) : null}

      {/* PR-C1 (ADR-0035) Phase 1 — «Вперёд» → существующий Договор по этому КП.
          Видна ТОЛЬКО когда forwardContractPath != null (контейнер нашёл saved
          contract, решение #1). Переоткрывает последний редактированный договор
          (решение #2). Реквизиты клиента целы (PR-B1 / ADR-0034). */}
      {forwardContractPath ? (
        <Link
          to={forwardContractPath}
          aria-label="Вперёд — открыть договор по этому КП"
          data-testid="quote-forward-contract-link"
          className="w-full min-h-14 rounded-2xl border-[1.5px] border-accent bg-card text-ink text-base font-medium flex items-center justify-center gap-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
        >
          Вперёд
          <ArrowRight size={22} aria-hidden="true" />
        </Link>
      ) : null}

      {saveStatus === 'success' && savedName ? (
        <p
          role="status"
          aria-live="polite"
          data-testid="quote-save-success"
          className="text-sm text-ink font-semibold text-center"
        >
          Сохранён: <strong>{savedName}</strong>
        </p>
      ) : null}

      {errorMessage ? (
        <p
          role="alert"
          data-testid="quote-save-error"
          className="text-sm text-destructive font-medium text-center"
        >
          {errorMessage}
        </p>
      ) : null}

      {shareNotification ? (
        <p
          role="status"
          aria-live="polite"
          data-testid="quote-share-notification"
          className={
            shareNotification.kind === 'success'
              ? 'text-sm text-ink font-semibold text-center'
              : 'text-sm text-ink text-center'
          }
        >
          {shareNotification.text}
        </p>
      ) : null}

      <ShareBottomSheet
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        state={state}
        quoteName={savedName ?? undefined}
        onResult={(result: ShareResult) => {
          if (result.status === 'shared') {
            setShareNotification({ kind: 'success', text: 'КП отправлено' });
          } else if (result.status === 'downloaded') {
            setShareNotification({
              kind: 'info',
              text: 'КП скачан. Текст скопирован — вставьте в чат.',
            });
          } else if (result.status === 'desktop-direct') {
            setShareNotification({
              kind: 'info',
              text: buildDesktopShareToast(result.channel),
            });
          } else {
            setShareNotification(null);
          }
        }}
      />
    </div>
  );
}
